import { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { ArrowLeft } from "lucide-react"
import { useServiceTicket, useCreateServiceTicket, useUpdateServiceTicket } from "@/hooks/use-service-tickets"
import { useTicketTypes } from "@/hooks/use-ticket-types"
import { useCompanies } from "@/hooks/use-companies"
import { ApiClientError } from "@/lib/api-client"
import { Button } from "@/components/ui/button"
import { toast } from "sonner"

const ticketSchema = z.object({
  title: z.string().min(1, "Tytuł jest wymagany"),
  ticket_type_id: z.string().min(1, "Typ serwisu jest wymagany"),
  phase_id: z.string().min(1, "Etap jest wymagany"),
  company_id: z.string().optional(),
  priority: z.string(),
  assigned_to_id: z.string().optional(),
  description: z.string().optional(),
})

type TicketForm = z.infer<typeof ticketSchema>

export function ServiceTicketFormPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = !!id
  const [error, setError] = useState<string | null>(null)

  const { data: ticketData } = useServiceTicket(id)
  const { data: ticketTypesData } = useTicketTypes()
  const { data: companiesData } = useCompanies()
  const createMutation = useCreateServiceTicket()
  const updateMutation = useUpdateServiceTicket()

  const ticketTypes = ticketTypesData?.data ?? []
  const companies = companiesData?.data ?? []

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<TicketForm>({
    resolver: zodResolver(ticketSchema),
    values: isEdit && ticketData ? {
      title: ticketData.data.title,
      ticket_type_id: ticketData.data.ticket_type.id,
      phase_id: ticketData.data.phase.id,
      company_id: ticketData.data.company?.id || "",
      priority: ticketData.data.priority,
      assigned_to_id: ticketData.data.assigned_to?.id || "",
      description: ticketData.data.description || "",
    } : {
      title: "",
      ticket_type_id: ticketTypes[0]?.id || "",
      phase_id: "",
      company_id: "",
      priority: "medium",
      assigned_to_id: "",
      description: "",
    },
  })

  const selectedTicketTypeId = watch("ticket_type_id")
  const selectedTicketType = ticketTypes.find((tt) => tt.id === selectedTicketTypeId)
  const availablePhases = selectedTicketType?.phases.filter(p => !p.is_resolved && !p.is_closed) ?? []

  useEffect(() => {
    if (!isEdit && availablePhases.length > 0 && !watch("phase_id")) {
      setValue("phase_id", availablePhases[0].id)
    }
  }, [selectedTicketTypeId, availablePhases, isEdit, setValue, watch])

  const onSubmit = async (data: TicketForm) => {
    try {
      setError(null)
      const payload: Record<string, unknown> = {
        ...data,
        company_id: data.company_id || null,
        assigned_to_id: data.assigned_to_id || null,
      }

      if (isEdit) {
        await updateMutation.mutateAsync({ id, data: payload })
        toast.success("Zgłoszenie zaktualizowane")
        navigate(`/service/${id}`)
      } else {
        const result = await createMutation.mutateAsync(payload)
        toast.success("Zgłoszenie utworzone")
        navigate(`/service/${result.data.id}`)
      }
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message)
      } else {
        setError("Wystąpił błąd")
      }
    }
  }

  const inputClass = "w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm text-foreground outline-none transition-colors focus:ring-2 focus:ring-offset-1"
  const labelClass = "mb-1.5 block text-sm font-medium text-foreground"

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(isEdit ? `/service/${id}` : "/service")} className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted/50">
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-xl font-semibold text-foreground">
          {isEdit ? "Edytuj zgłoszenie" : "Nowe zgłoszenie serwisowe"}
        </h1>
      </div>

      {error && (
        <div className="rounded-md border border-red-500 p-3 text-sm text-red-500 bg-red-500/5">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Title */}
        <div>
          <label className={labelClass}>Tytuł *</label>
          <input
            {...register("title")}
            className={`${inputClass} ${errors.title ? "border-red-500" : ""}`}
            placeholder="Opis zgłoszenia serwisowego"
          />
          {errors.title && <p className="mt-1 text-xs text-red-500">{errors.title.message}</p>}
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Ticket Type */}
          <div>
            <label className={labelClass}>Typ serwisu *</label>
            <select
              {...register("ticket_type_id")}
              className={inputClass}
            >
              <option value="">Wybierz typ...</option>
              {ticketTypes.map((tt) => (
                <option key={tt.id} value={tt.id}>{tt.name}</option>
              ))}
            </select>
          </div>

          {/* Phase */}
          <div>
            <label className={labelClass}>Etap *</label>
            <select
              {...register("phase_id")}
              className={inputClass}
            >
              <option value="">Wybierz etap...</option>
              {availablePhases.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Priority */}
          <div>
            <label className={labelClass}>Priorytet</label>
            <select
              {...register("priority")}
              className={inputClass}
            >
              <option value="low">Niski</option>
              <option value="medium">Średni</option>
              <option value="high">Wysoki</option>
              <option value="urgent">Pilny</option>
            </select>
          </div>

          {/* Company */}
          <div>
            <label className={labelClass}>Kontrahent</label>
            <select
              {...register("company_id")}
              className={inputClass}
            >
              <option value="">Brak</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Description */}
        <div>
          <label className={labelClass}>Opis</label>
          <textarea
            {...register("description")}
            className={inputClass}
            style={{ minHeight: 120 }}
            placeholder="Szczegółowy opis problemu..."
          />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Zapisywanie..." : isEdit ? "Zapisz zmiany" : "Utwórz zgłoszenie"}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate(isEdit ? `/service/${id}` : "/service")}>
            Anuluj
          </Button>
        </div>
      </form>
    </div>
  )
}
